import { parseProject, type Project, type ProjectId } from '@finsim/model';
import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { debounce } from 'patronum';
import { ApiRequestError, NetworkError } from '../../shared/api/http';
import type { ProjectMeta, SyncMeta } from '../../shared/api/local-projects';
import { remoteProjects, type RemoteMeta } from '../../shared/api/remote-projects';
import { newId } from '../../shared/lib/id';
import { $project, projectClosed, projectLoaded } from '../project';
import { authApi } from '../../shared/api/auth';
import { $user, checkSessionFx, logoutFx, signedOut, signOutRequested } from '../session';
import { $library, libraryRequested, saveProjectFx } from './model';
import { storeRemote } from './remote-copy';
import { local } from './store';

/*
 * Sync with the account (docs/PLAN.md §3.5, M7). IndexedDB stays the source the app edits; it is
 * also the outbox. Each local project has a sync record (see SyncMeta):
 * - saving marks the user's projects dirty; dirty projects are uploaded shortly after (create, or
 *   update with If-Match on the version they're based on);
 * - a 409 means another device saved first: the user chooses (keep mine / take theirs / both);
 * - pulling downloads newer versions of projects without local edits, and removes projects deleted
 *   elsewhere;
 * - offline, uploads wait and are retried when the browser is back online;
 * - on first sign-in, local-only projects can be imported into the account.
 */

export const PUSH_DEBOUNCE_MS = 1000;

export type PushResult =
  | { id: string; outcome: 'skipped' | 'clean' | 'pushed' | 'offline' | 'gone' }
  | { id: string; outcome: 'conflict'; project: Project; remote: RemoteMeta };

export interface Conflict {
  project: Project;
  remote: RemoteMeta;
}

/** Sync state of the open project, for the status indicator. */
export type OpenSyncState =
  'none' | 'localOnly' | 'synced' | 'pending' | 'syncing' | 'offline' | 'conflict';

const readLocal = async (id: string): Promise<Project | null> => {
  const parsed = parseProject(await local().load(id));
  return parsed.success ? parsed.data : null;
};

// ── Recording local saves ───────────────────────────────────────────────────────

/**
 * After a local save: a project without a sync record is claimed by the signed-in user (or marked
 * local-only when anonymous); the user's projects become dirty.
 */
export const recordSaveFx = createEffect(
  async ({ id, userId }: { id: string; userId: string | null }): Promise<boolean> => {
    const meta = await local().getSync(id);
    if (!meta) {
      await local().setSync(
        id,
        userId
          ? { ownerId: userId, remoteVersion: null, dirty: true }
          : { ownerId: null, remoteVersion: null, dirty: false },
      );
      return userId !== null;
    }
    if (userId === null || meta.ownerId !== userId) return false;
    if (!meta.dirty) await local().setSync(id, { ...meta, dirty: true });
    return true;
  },
);

// ── Upload ──────────────────────────────────────────────────────────────────────

/** Uploads one project if it's the user's and dirty (plain function: shared by the effects). */
async function push(id: string, userId: string): Promise<PushResult> {
  const meta = await local().getSync(id);
  if (meta?.ownerId !== userId) return { id, outcome: 'skipped' };
  if (!meta.dirty) return { id, outcome: 'clean' };
  const project = await readLocal(id);
  if (!project) return { id, outcome: 'skipped' };
  try {
    const version =
      meta.remoteVersion === null
        ? (await remoteProjects.create(project)).project.version
        : (await remoteProjects.update(project, meta.remoteVersion)).meta.version;
    // Edited again while uploading? Then it stays dirty and is pushed again.
    const latest = await local().load(id);
    const dirty = JSON.stringify(latest) !== JSON.stringify(project);
    await local().setSync(id, { ownerId: userId, remoteVersion: version, dirty });
    return { id, outcome: 'pushed' };
  } catch (error) {
    if (error instanceof NetworkError) return { id, outcome: 'offline' };
    if (error instanceof ApiRequestError) {
      if (error.status === 409 && error.code === 'conflict') {
        return { id, outcome: 'conflict', project, remote: error.body.meta as RemoteMeta };
      }
      if (error.status === 409 && error.code === 'exists') {
        // Created earlier (e.g. an upload whose response was lost): compare with that version.
        const remote = await remoteProjects.get(id);
        return { id, outcome: 'conflict', project, remote: remote.meta };
      }
      if (error.status === 404) {
        // Deleted on another device: keep this copy as a local-only project.
        await local().setSync(id, { ownerId: null, remoteVersion: null, dirty: false });
        return { id, outcome: 'gone' };
      }
      if (error.status === 401) return { id, outcome: 'offline' };
    }
    throw error;
  }
}

async function pushAll(userId: string): Promise<PushResult[]> {
  const results: PushResult[] = [];
  for (const meta of await local().list()) results.push(await push(meta.id, userId));
  return results;
}

export const pushFx = createEffect(({ id, userId }: { id: string; userId: string }) =>
  push(id, userId),
);

/** Uploads every dirty project of the user (after sign-in, when back online). */
export const pushAllFx = createEffect(pushAll);

// ── Download ────────────────────────────────────────────────────────────────────

/**
 * Brings the local library up to date with the account: downloads projects that are new or newer
 * (unless edited locally) and removes clean copies of projects deleted elsewhere.
 */
export const pullFx = createEffect(async (userId: string): Promise<ProjectMeta[]> => {
  const remote = await remoteProjects.list();
  const remoteIds = new Set(remote.map((r) => r.id));
  for (const r of remote) {
    const meta = await local().getSync(r.id);
    if (meta?.dirty || (meta && meta.ownerId !== userId)) continue;
    if (meta?.remoteVersion != null && meta.remoteVersion >= r.version) continue;
    const { project } = await remoteProjects.get(r.id);
    const parsed = parseProject(project);
    if (parsed.success) await storeRemote(parsed.data, r, userId);
  }
  for (const { id } of await local().list()) {
    const meta = await local().getSync(id);
    if (
      meta?.ownerId === userId &&
      !meta.dirty &&
      meta.remoteVersion !== null &&
      !remoteIds.has(id)
    ) {
      await local().remove(id);
    }
  }
  return local().list();
});

/** On opening a clean synced project: a newer remote version replaces the local copy. */
export const refreshOpenFx = createEffect(
  async ({ project, userId }: { project: Project; userId: string }): Promise<Project | null> => {
    const meta = await local().getSync(project.id);
    if (meta?.ownerId !== userId || meta.dirty || meta.remoteVersion === null) return null;
    const remote = await remoteProjects.get(project.id);
    if (remote.meta.version <= meta.remoteVersion) return null;
    const parsed = parseProject(remote.project);
    if (!parsed.success) return null;
    await storeRemote(parsed.data, remote.meta, userId);
    return parsed.data;
  },
);

// ── Conflicts ───────────────────────────────────────────────────────────────────

export type ConflictChoice = 'mine' | 'theirs' | 'both';

export const conflictResolved = createEvent<{ choice: ConflictChoice; copyName: string }>();

/**
 * - mine: overwrite the server's version with the local one;
 * - theirs: replace the local copy with the server's;
 * - both: take theirs, and keep mine as a new project.
 * Returns the project to show if the open one changed.
 */
export const resolveConflictFx = createEffect(
  async ({
    conflict,
    choice,
    copyName,
    userId,
  }: {
    conflict: Conflict;
    choice: ConflictChoice;
    copyName: string;
    userId: string;
  }): Promise<Project | null> => {
    const { project } = conflict;
    if (choice === 'mine') {
      const { meta } = await remoteProjects.update(project, conflict.remote.version);
      await local().setSync(project.id, {
        ownerId: userId,
        remoteVersion: meta.version,
        dirty: false,
      });
      return null;
    }
    if (choice === 'both') {
      const copy: Project = { ...project, id: newId('prj') as ProjectId, name: copyName };
      await local().save(copy, Date.now());
      await local().setSync(copy.id, { ownerId: userId, remoteVersion: null, dirty: true });
    }
    const remote = await remoteProjects.get(project.id);
    const parsed = parseProject(remote.project);
    if (!parsed.success) return null;
    await storeRemote(parsed.data, remote.meta, userId);
    return parsed.data;
  },
);

// ── Import on first sign-in ───────────────────────────────────────────────────

const dismissedKey = (userId: string): string => `finsim:import-dismissed:${userId}`;

/** Local-only projects that could be imported into the account (unless the user said no). */
export const findImportableFx = createEffect(async (userId: string): Promise<ProjectMeta[]> => {
  try {
    if (localStorage.getItem(dismissedKey(userId)) === '1') return [];
  } catch {
    // Storage unavailable: offer anyway.
  }
  const importable: ProjectMeta[] = [];
  for (const meta of await local().list()) {
    const sync = await local().getSync(meta.id);
    // No record, or explicitly local-only.
    if ((sync?.ownerId ?? null) === null) importable.push(meta);
  }
  return importable;
});

/** Uploads the chosen local projects; content the account already has is not duplicated. */
export const importFx = createEffect(
  async ({ ids, userId }: { ids: string[]; userId: string }): Promise<number> => {
    let imported = 0;
    for (const id of ids) {
      const project = await readLocal(id);
      if (!project) continue;
      const { project: meta } = await remoteProjects.create(project);
      await local().setSync(id, { ownerId: userId, remoteVersion: meta.version, dirty: false });
      imported++;
    }
    return imported;
  },
);

export const importDismissed = createEvent();
export const importConfirmed = createEvent<string[]>();

const dismissImportFx = createEffect((userId: string) => {
  try {
    localStorage.setItem(dismissedKey(userId), '1');
  } catch {
    // Not remembered: the offer comes back next time.
  }
});

// ── Sign-out ────────────────────────────────────────────────────────────────────

/**
 * Before signing out: upload what's pending; after: remove local copies of the account's projects
 * (they're in the cloud). Anything that couldn't be uploaded stays, as a local-only project.
 */
export const signOutFx = createEffect(async (userId: string): Promise<ProjectMeta[]> => {
  try {
    await pushAll(userId);
  } catch {
    // Best effort.
  }
  await authApi.logout();
  for (const { id } of await local().list()) {
    const meta = await local().getSync(id);
    if (meta?.ownerId !== userId) continue;
    if (meta.dirty) await local().setSync(id, { ownerId: null, remoteVersion: null, dirty: false });
    else await local().remove(id);
  }
  return local().list();
});

/**
 * Deletes a project here and, if it's synced, moves it to the account's trash first (offline, the
 * delete fails rather than letting the next sync bring the project back).
 */
export const deleteEverywhereFx = createEffect(
  async ({ id, userId }: { id: string; userId: string | null }): Promise<ProjectMeta[]> => {
    const meta = await local().getSync(id);
    if (userId && meta?.ownerId === userId && meta.remoteVersion !== null) {
      await remoteProjects.remove(id);
    }
    return local().remove(id);
  },
);
export const deleteRequested = createEvent<string>();

// ── Trash (account) ──────────────────────────────────────────────────────────────

export const trashRequested = createEvent();
export const restoreRequested = createEvent<string>();
export const listTrashFx = createEffect(() => remoteProjects.list(true));
export const restoreFx = createEffect((id: string) => remoteProjects.restore(id));

export const $trash = createStore<RemoteMeta[]>([])
  .on(listTrashFx.doneData, (_, list) => list)
  .on(restoreFx.done, (list, { params }) => list.filter((m) => m.id !== params))
  .reset(logoutFx.done, signedOut);

// ── Wiring ──────────────────────────────────────────────────────────────────────

export const onlineChanged = createEvent<boolean>();

const $userId = $user.map((u) => u?.id ?? null);

/** Dirty projects waiting to be uploaded (or offline). */
export const $conflict = createStore<Conflict | null>(null).reset(
  projectClosed,
  logoutFx.done,
  signedOut,
);
export const $importOffer = createStore<ProjectMeta[]>([])
  .on(findImportableFx.doneData, (_, list) => list)
  .reset(importFx.done, importDismissed, logoutFx.done, signedOut);

export const $openSync = createStore<OpenSyncState>('none').reset(projectClosed);

// Local saves → sync records → debounced upload of the open project.
sample({
  clock: saveProjectFx.done,
  source: $userId,
  fn: (userId, { params }) => ({ id: params.id, userId }),
  target: recordSaveFx,
});
const openDirty = sample({
  clock: recordSaveFx.done,
  source: $project,
  filter: (project, { params, result }) => result && project?.id === params.id,
  fn: (_, { params }) => params,
});
sample({ clock: openDirty, fn: () => 'pending' as const, target: $openSync });
sample({
  clock: debounce(openDirty, PUSH_DEBOUNCE_MS),
  filter: (p): p is { id: string; userId: string } => p.userId !== null,
  target: pushFx,
});

// Results of uploads.
sample({
  clock: pushFx,
  source: $project,
  filter: (project, { id }) => project?.id === id,
  fn: () => 'syncing' as const,
  target: $openSync,
});
sample({
  clock: pushFx.doneData,
  source: $project,
  filter: (project, r) => project?.id === r.id && r.outcome !== 'skipped',
  fn: (_, r): OpenSyncState =>
    r.outcome === 'offline'
      ? 'offline'
      : r.outcome === 'conflict'
        ? 'conflict'
        : r.outcome === 'gone'
          ? 'localOnly'
          : 'synced',
  target: $openSync,
});
sample({
  clock: pushAllFx.doneData.filterMap((results) => {
    const r = results.find((x) => x.outcome === 'conflict');
    return r?.outcome === 'conflict' ? { project: r.project, remote: r.remote } : undefined;
  }),
  target: $conflict,
});
sample({
  clock: pushFx.doneData.filterMap((r) =>
    r.outcome === 'conflict' ? { project: r.project, remote: r.remote } : undefined,
  ),
  target: $conflict,
});

// Opening a project: its sync state, and a newer remote version if there is one.
const openedSignedIn = sample({
  clock: projectLoaded,
  source: $userId,
  filter: (userId): userId is string => userId !== null,
  fn: (userId, project) => ({ project, userId: userId as string }),
});
export const loadOpenSyncFx = createEffect(
  async ({ project, userId }: { project: Project; userId: string }): Promise<OpenSyncState> => {
    const meta = await local().getSync(project.id);
    if (meta?.ownerId !== userId) return 'localOnly';
    return meta.dirty ? 'pending' : 'synced';
  },
);
sample({ clock: openedSignedIn, target: [loadOpenSyncFx, refreshOpenFx] });
sample({ clock: loadOpenSyncFx.doneData, target: $openSync });
sample({
  clock: refreshOpenFx.doneData,
  filter: (project): project is Project => project !== null,
  target: projectLoaded,
});

// Signed in (at startup or after signing in): pull, push, and offer to import local projects.
const signedIn = checkSessionFx.doneData.filterMap((user) => user?.id);
sample({ clock: signedIn, target: [pullFx, pushAllFx, findImportableFx] });
// A project opened before the session was known (e.g. right after a reload): check it now.
sample({
  clock: signedIn,
  source: $project,
  filter: (project): project is Project => project !== null,
  fn: (project, userId) => ({ project: project as Project, userId }),
  target: [loadOpenSyncFx, refreshOpenFx],
});
sample({
  clock: libraryRequested,
  source: $userId,
  filter: (userId): userId is string => userId !== null,
  target: pullFx,
});
sample({ clock: pullFx.doneData, target: $library });
sample({
  clock: onlineChanged,
  source: $userId,
  filter: (userId, online): userId is string => online && userId !== null,
  target: pushAllFx,
});
sample({
  clock: pushAllFx.doneData,
  source: $project,
  filter: (project, results) =>
    results.some((r) => r.id === project?.id && r.outcome !== 'skipped'),
  fn: (project, results) => {
    const r = results.find((x) => x.id === project?.id);
    return r?.outcome === 'offline' ? ('offline' as const) : ('synced' as const);
  },
  target: $openSync,
});

// Conflicts.
sample({
  clock: conflictResolved,
  source: { conflict: $conflict, userId: $userId },
  filter: ({ conflict, userId }) => conflict !== null && userId !== null,
  fn: ({ conflict, userId }, { choice, copyName }) => ({
    conflict: conflict as Conflict,
    userId: userId as string,
    choice,
    copyName,
  }),
  target: resolveConflictFx,
});
$conflict.reset(resolveConflictFx.done);
sample({ clock: resolveConflictFx.done, fn: () => 'synced' as const, target: $openSync });
sample({
  clock: resolveConflictFx.doneData,
  source: $project,
  filter: (open, next) => next !== null && open?.id === next.id,
  fn: (_, next) => next as Project,
  target: projectLoaded,
});
sample({ clock: resolveConflictFx.done, target: libraryRequested });

// Import.
sample({
  clock: importConfirmed,
  source: $userId,
  filter: (userId): userId is string => userId !== null,
  fn: (userId, ids) => ({ ids, userId: userId as string }),
  target: importFx,
});
sample({ clock: importFx.done, target: libraryRequested });
// The open project was among them: it's in the account now.
sample({
  clock: importFx.done,
  source: $project,
  filter: (project, { params }) => project !== null && params.ids.includes(project.id),
  fn: () => 'synced' as const,
  target: $openSync,
});
sample({
  clock: importDismissed,
  source: $userId,
  filter: (userId): userId is string => userId !== null,
  target: dismissImportFx,
});

// Sign-out.
sample({
  clock: signOutRequested,
  source: $userId,
  filter: (userId): userId is string => userId !== null,
  target: signOutFx,
});
sample({ clock: signOutFx.doneData, target: $library });
sample({ clock: signOutFx.done, target: signedOut });
sample({ clock: signOutFx.done, fn: () => 'none' as const, target: $openSync });

// Deleting a project also trashes it in the account.
sample({
  clock: deleteRequested,
  source: $userId,
  fn: (userId, id) => ({ id, userId }),
  target: deleteEverywhereFx,
});
sample({ clock: deleteEverywhereFx.doneData, target: $library });

// Account trash.
sample({ clock: trashRequested, target: listTrashFx });
sample({ clock: restoreRequested, target: restoreFx });
sample({ clock: restoreFx.done, target: libraryRequested });

/** Whether any sync work is in flight (for the status indicator). */
export const $syncing = combine(
  pushFx.pending,
  pullFx.pending,
  importFx.pending,
  (push, pull, imp) => push || pull || imp,
);

/** Sync records of the listed projects (home badges: "this browser only"). */
export const loadSyncMetasFx = createEffect(async (list: ProjectMeta[]) => {
  const entries = await Promise.all(
    list.map(async (m) => [m.id, (await local().getSync(m.id)) ?? null] as const),
  );
  return Object.fromEntries(entries);
});
export const $syncMetas = createStore<Record<string, SyncMeta | null>>({}).on(
  loadSyncMetasFx.doneData,
  (_, metas) => metas,
);
sample({ clock: $library, target: loadSyncMetasFx });
sample({
  clock: [importFx.done, pushFx.done, pushAllFx.done],
  source: $library,
  target: loadSyncMetasFx,
});
